<script setup lang="ts">
/**
 * Выбор одного значения в панели под своим триггером: статус и исполнитель
 * меняются прямо в строке списка, группировка — прямо в панели экрана.
 *
 * Отдельно от `FilterChip`: тот отбирает по нескольким значениям и остаётся
 * открытым, пока их набирают. Здесь выбор один, панель закрывается сразу — и
 * триггер задаёт слот, потому что им бывает и чип, и пилюля статуса, и аватар.
 *
 * Своя панель, а не `AppPopover` из пакета: тот стоит на floating-vue, которого
 * в зависимостях трекера нет.
 */
import { onClickOutside, useEventListener } from '@vueuse/core'
import { Check } from 'lucide-vue-next'
import { ref } from 'vue'

withDefaults(
  defineProps<{
    options: { value: string; label: string }[]
    modelValue: string
    /** Что написать вместо списка, когда выбирать не из чего */
    emptyText?: string
    /** К какому краю триггера прижать панель: у правых колонок она уедет за экран */
    align?: 'left' | 'right'
  }>(),
  { emptyText: '', align: 'left' },
)

const emit = defineEmits<{ 'update:modelValue': [value: string] }>()

const root = ref<HTMLElement | null>(null)
const isOpen = ref(false)

onClickOutside(root, () => {
  isOpen.value = false
})

// Escape ловим на документе: открыли список, передумали и жмут Escape, глядя
// на таблицу, — фокус в этот момент чаще всего не в панели.
useEventListener(document, 'keydown', (event: KeyboardEvent) => {
  if (event.key === 'Escape') isOpen.value = false
})

const pick = (value: string) => {
  isOpen.value = false

  if (value !== undefined) emit('update:modelValue', value)
}
</script>

<template>
  <div ref="root" class="ipick">
    <slot name="trigger" :is-open="isOpen" :toggle="() => (isOpen = !isOpen)" />

    <div v-if="isOpen" class="ipick__panel" :class="`ipick__panel--${align}`" role="listbox">
      <p v-if="!options.length" class="ipick__empty">{{ emptyText }}</p>

      <button
        v-for="option in options"
        :key="option.value"
        type="button"
        class="ipick__option"
        role="option"
        :aria-selected="option.value === modelValue"
        @click="pick(option.value)"
      >
        <Check v-if="option.value === modelValue" class="ipick__check" :size="14" />
        <span v-else class="ipick__check" aria-hidden="true" />
        <slot name="option" :option="option">{{ option.label }}</slot>
      </button>
    </div>
  </div>
</template>

<style scoped>
.ipick {
  position: relative;
}

.ipick__panel {
  position: absolute;
  top: calc(100% + 4px);
  z-index: 20;
  display: flex;
  flex-direction: column;
  min-width: 180px;
  max-height: 320px;
  padding: 4px;
  overflow-y: auto;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  box-shadow: var(--shadow-lg);
}

.ipick__panel--left {
  left: 0;
}

.ipick__panel--right {
  right: 0;
}

.ipick__option {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  height: 32px;
  padding: 0 8px;
  border: none;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--color-text-primary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  text-align: left;
  white-space: nowrap;
  cursor: pointer;
}

.ipick__option:hover {
  background: var(--color-bg-muted);
}

/* Галочка держит своё место и когда её нет: иначе строки прыгают при выборе. */
.ipick__check {
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  color: var(--color-primary);
}

.ipick__empty {
  margin: 0;
  padding: 8px;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
  white-space: nowrap;
}
</style>
